import { Component, Ref, ref } from 'vue'
import components from './components/index'
import { generateId } from '@/utils/helpers.js'
import { cloneDeep } from 'lodash'
import { Empty } from '@/utils/types/helpers'
import { dictionary } from '@/utils/key-to-label/dictionary'

interface DialogComponent {
  component: Component
  title: (_dialog: Dialog) => string | undefined
  subtitle?: (_dialog: Dialog) => string | undefined
  width?: string | number
  submitLabel?: string
}

type DialogComponentsNames =
  | 'Document'
  | 'DocumentSignature'
  | 'DocumentRouteSignatures'
  | 'Order'
  | 'UserPhoto'
  | 'TemplateGroup'
  | 'User'
  | 'UserCard'
  | 'Absence'
  | 'RelatedOrder'
  | 'ChangeUser'
  | 'TemplateRoutingDocument'
  | 'Department'
  | 'ChangeDepartment'
  | 'FamiliarizationReport'
  | 'SimpleDialog'
  | 'Textarea'
  | 'Table'
  | 'DocumentAgreementUsers'
  | 'DocumentAgreementHistory'
  | 'DocumentAcceptanceParticipants'
  | 'FamiliarizationUsers'
  | 'Comments'
  | 'OrderDeactivate'
  | 'Contractor'
  | 'ContractorDetails'
  | 'ContractorContact'
  | 'Correspondence'
  | 'CorrespondenceDeliver'
  | 'CorrespondenceDeliveryInfo'
  | 'CorrespondenceTasks'
  | 'PerformerUsers'
  | 'RelatedCorrespondence'
  | 'RelatedTask'
  | 'CopyTask'
  | 'CreateTask'
  | 'TaskDelegation'
  | 'Notes'
  | 'ClientAccess'
  | 'SignDocument'
  | 'SignDiia'
  | 'DocumentSendToSign'
  | 'DocumentSendToReview'
  | 'DocumentCancel'
  | 'Audit'
  | 'AuditRecommendation'
  | 'AuditRecommendationSetStatus'
  | 'AuditComplete'
  | 'AuditFlaw'
  | 'AuditRisk'
  | 'IncidentRiskAssessment'
  | 'IncidentIssue'
  | 'IncidentResolution'
  | 'SelectDepartmentPerson'
  | 'TemplateSelect'
  | 'SelectDocumentAgreement'
  | 'UserSelect'
  | 'Protocol'
  | 'RelatedProtocol'
  | 'ProtocolDeactivate'
  | 'Role'
  | 'OrgDirectoryItem'
  | 'InternalDepartment'
  | 'InternalStaffUnit'
  | 'StaffUnitAssignment'
  | 'Plan'
  | 'AuditAttach'
  | 'DocTypeAccess'
  | 'DocTypeField'
  | 'DocTypeClass'
  | 'FieldDefinition'
  | 'DocTypeDefinition'
  | 'Organization'
  | 'DocTypeRoute'
  | 'RegistrationJournal'
  | 'DocTypeRouteStage'
  | 'MaskPlaceholder'
  | 'Delegation'
  | 'DelegationCreate'
  | 'DocumentAcceptance'
  | 'DelegationSettings'
  | 'DelegationRestriction'
  | 'DocumentRelation'
  | 'DocumentRevision'
  | 'DocumentRegistration'
  | 'DocumentReturnFromReview'
  | 'DocumentChangeReviewer'
  | 'IncidentExecutor'
  | 'IncidentSetStatus'
  | 'IncidentIssueTask'
  | 'IncidentIssueSetStatus'

type DialogComponents = Record<DialogComponentsNames, DialogComponent>

type DialogCallback<TResult = unknown> = (_result: TResult) => unknown

const getDialogSubmitPayload = <TResult>(
  result: TResult | { data?: TResult }
): TResult => {
  if (typeof result === 'object' && result !== null && 'data' in result) {
    return (result.data ?? result) as TResult
  }

  return result
}

interface DialogRef<TResult = any> {
  validationOverrides?: Record<string, string>
  submit: () => TResult
  validationHandler?: () => unknown
  validationScope?: keyof typeof dictionary
  v$: any
  disabled?: boolean
}

interface Dialog<
  TDialogData extends Record<string, any> = Record<string, any>,
  TResult = any
> {
  isOpen?: boolean
  loading: boolean
  name: keyof DialogComponents | null
  action?: string | null
  params: {
    cb?: DialogCallback<TResult>
    title?: string
    subtitle?: string
    submitLabel?: string
    cancelLabel?: string
    hideActionButtons?: boolean
    validationScope?: keyof typeof dictionary
  }
  dialogData: TDialogData
  id?: string
  ref?: DialogRef<TResult> | null
  close: () => void
  submit: (_res: TResult | { data?: TResult }) => Promise<void>
}

const getDialogInstance = () => {
  const dialogs: Ref<Dialog[]> = ref([])

  const defaultDialog: Dialog = {
    isOpen: false,
    loading: true,
    name: null,
    action: null,
    params: {},
    dialogData: {},
    close() {
      closeDialog(this)
    },
    async submit(res) {
      try {
        if (typeof this.params.cb === 'function') {
          await this.params.cb(getDialogSubmitPayload(res))
        }
        this.close()
      } catch (e) {
        throw new Error(
          `
            dialog: ${this.name}.vue has unexpected error on submit action
            Error: ${e}
          `
        )
      }
    },
  }

  function setDialogRef(dialog: Dialog) {
    return (componentRef: any) => {
      dialog.ref = componentRef
    }
  }

  function openDialog(
    dialogData: Empty<Dialog> & { name: DialogComponentsNames }
  ) {
    if (Object.keys(components).includes(dialogData.name)) {
      const id = generateId()
      const dialog = {
        ...defaultDialog,
        ...cloneDeep(dialogData),
        isOpen: true,
        ref: null,
        id,
      }
      dialogs.value.push(dialog)
    } else {
      throw new Error(`Invalid dialog name - ${dialogData.name}`)
    }
  }

  function closeDialog(dialog: Dialog) {
    dialog.isOpen = false
    setTimeout(() => {
      dialogs.value = dialogs.value.filter(d => d.id !== dialog.id)
    }, 500)
  }

  function closeDialogs() {
    dialogs.value.splice(0)
  }

  return { openDialog, closeDialog, dialogs, setDialogRef, closeDialogs }
}

const dialogInstance = getDialogInstance()

const useDialog = () => dialogInstance

const openDialog = dialogInstance.openDialog
const closeDialog = dialogInstance.closeDialogs

export { useDialog, openDialog, closeDialog, DialogComponents, Dialog }
